// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import type {
  EclecticHoleCell,
  EclecticPlayerStanding,
  EclecticSection,
  FreezeHoleCell,
  FreezePlayerStanding,
  FreezeSection,
  TourGameRound,
} from './types';

type Raw = Record<string, unknown>;

function asObject(value: unknown): Raw | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Raw) : null;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function pick(raw: Raw, camel: string): unknown {
  if (raw[camel] !== undefined) return raw[camel];
  const pascal = camel.charAt(0).toUpperCase() + camel.slice(1);
  return raw[pascal];
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function asNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function asNullableNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function asBoolean(value: unknown): boolean {
  return value === true;
}

function normalizeRounds(value: unknown): TourGameRound[] {
  return asArray(value)
    .map(asObject)
    .filter((round): round is Raw => round !== null)
    .map((round) => ({
      roundId: asString(pick(round, 'roundId')) ?? '',
      courseName: asString(pick(round, 'courseName')) ?? '',
      status: asString(pick(round, 'status')) ?? '',
      holesCount: asNumber(pick(round, 'holesCount'), 18),
    }))
    .filter((round) => round.roundId.length > 0);
}

function normalizeEclecticHole(raw: Raw): EclecticHoleCell {
  return {
    holeNumber: asNumber(pick(raw, 'holeNumber')),
    bestPoints: asNumber(pick(raw, 'bestPoints')),
    bestRoundId: asString(pick(raw, 'bestRoundId')),
    pointsByRound: asArray(pick(raw, 'pointsByRound'))
      .map(asObject)
      .filter((entry): entry is Raw => entry !== null)
      .map((entry) => ({
        roundId: asString(pick(entry, 'roundId')) ?? '',
        points: asNumber(pick(entry, 'points')),
      }))
      .filter((entry) => entry.roundId.length > 0),
  };
}

function normalizeEclecticStanding(raw: Raw): EclecticPlayerStanding | null {
  const playerId = asString(pick(raw, 'playerId'));
  if (!playerId) return null;
  return {
    playerId,
    totalPoints: asNumber(pick(raw, 'totalPoints')),
    position: asNumber(pick(raw, 'position')),
    holes: asArray(pick(raw, 'holes'))
      .map(asObject)
      .filter((hole): hole is Raw => hole !== null)
      .map(normalizeEclecticHole),
  };
}

export function normalizeEclecticSection(value: unknown): EclecticSection | null {
  const raw = asObject(value);
  if (!raw) return null;
  const standings = asArray(pick(raw, 'standings'))
    .map(asObject)
    .filter((standing): standing is Raw => standing !== null)
    .map(normalizeEclecticStanding)
    .filter((standing): standing is EclecticPlayerStanding => standing !== null);
  if (standings.length === 0) return null;
  return {
    tourId: asString(pick(raw, 'tourId')) ?? '',
    gameId: asString(pick(raw, 'gameDefinitionId')) ?? '',
    isFinal: asBoolean(pick(raw, 'isFinal')),
    isFinalRound: asBoolean(pick(raw, 'isFinalRound')),
    holeCount: asNumber(pick(raw, 'holeCount'), 18),
    rounds: normalizeRounds(pick(raw, 'rounds')),
    standings,
  };
}

function normalizeFreezeHole(raw: Raw): FreezeHoleCell {
  return {
    holeNumber: asNumber(pick(raw, 'holeNumber')),
    locked: asBoolean(pick(raw, 'locked')),
    points: asNullableNumber(pick(raw, 'points')),
    sourceRoundId: asString(pick(raw, 'sourceRoundId')),
    lockable: asBoolean(pick(raw, 'lockable')),
    stale: asBoolean(pick(raw, 'stale')),
    pointsByRound: asArray(pick(raw, 'pointsByRound'))
      .map(asObject)
      .filter((entry): entry is Raw => entry !== null)
      .map((entry) => ({
        roundId: asString(pick(entry, 'roundId')) ?? '',
        points: asNumber(pick(entry, 'points')),
      }))
      .filter((entry) => entry.roundId.length > 0),
  };
}

function normalizeFreezeStanding(raw: Raw): FreezePlayerStanding | null {
  const playerId = asString(pick(raw, 'playerId'));
  if (!playerId) return null;
  const windowRaw = asObject(pick(raw, 'window'));
  return {
    playerId,
    totalPoints: asNumber(pick(raw, 'totalPoints')),
    lockedPoints: asNumber(pick(raw, 'lockedPoints')),
    unlockedPoints: asNumber(pick(raw, 'unlockedPoints')),
    lockedHoleCount: asNumber(pick(raw, 'lockedHoleCount')),
    position: asNumber(pick(raw, 'position')),
    window: {
      isOpen: windowRaw ? asBoolean(pick(windowRaw, 'isOpen')) : false,
      sourceRoundId: windowRaw ? asString(pick(windowRaw, 'sourceRoundId')) : null,
      closedByRoundId: windowRaw ? asString(pick(windowRaw, 'closedByRoundId')) : null,
    },
    holes: asArray(pick(raw, 'holes'))
      .map(asObject)
      .filter((hole): hole is Raw => hole !== null)
      .map(normalizeFreezeHole),
  };
}

export function normalizeFreezeSection(value: unknown): FreezeSection | null {
  const raw = asObject(value);
  if (!raw) return null;
  const standings = asArray(pick(raw, 'standings'))
    .map(asObject)
    .filter((standing): standing is Raw => standing !== null)
    .map(normalizeFreezeStanding)
    .filter((standing): standing is FreezePlayerStanding => standing !== null);
  if (standings.length === 0) return null;
  return {
    tourId: asString(pick(raw, 'tourId')) ?? '',
    gameId: asString(pick(raw, 'gameDefinitionId')) ?? '',
    isFinal: asBoolean(pick(raw, 'isFinal')),
    isFinalRound: asBoolean(pick(raw, 'isFinalRound')),
    holeCount: asNumber(pick(raw, 'holeCount'), 18),
    rounds: normalizeRounds(pick(raw, 'rounds')),
    standings,
  };
}
